import { DateTime } from "luxon";

import i18n from "../i18n";
import {
  ReturnActionKind,
  ReturnCondition,
  ReturnDeadline,
  ReturnDeadlineKind,
  ReturnDeadlineTone,
  ReturnDetail,
  ReturnReasonGroup,
  ReturnStage,
} from "../models/returns/IReturnsApi";
import { buyerFirstName, fillMergeFields } from "./fillMergeFields";
import formatDateTime from "./formatDateTime";
import { buyerFacingDate, INBOX_DAY, INBOX_DAY_TIME } from "./inboxDates";
import PriceFormat from "./priceFormat";

// The codes are eBay's and never translated; only the words shown to people are.
const tr = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, { ns: "returns", ...options });

/** eBay `ReturnStateEnum` → chip text (spec §3). Null = a manual (PartPro / counter) return. */
const STATE_LABELS: Record<string, string> = {
  RETURN_REQUESTED: "Waiting for your response",
  RETURN_REJECTED: "Declined",
  ITEM_READY_TO_SHIP: "Accepted – waiting for buyer to post",
  RETURN_LABEL_REQUESTED: "Buyer waiting for a label",
  RETURN_LABEL_PENDING: "Buyer waiting for a label",
  RMA_PENDING: "RMA needed",
  ITEM_SHIPPED: "Buyer has posted it",
  ITEM_DELIVERED: "Delivered back to you",
  ITEM_KEPT: "Buyer keeps the item",
  PARTIAL_REFUND_REQUESTED: "Partial refund offered – waiting for buyer",
  PARTIAL_REFUND_DECLINED: "Buyer declined the partial refund",
  REFUND_INITIATED: "Refund in progress",
  PARTIAL_REFUND_INITIATED: "Refund in progress",
  LESS_THAN_A_FULL_REFUND_ISSUED: "Partial refund issued",
  AUTO_REFUND_INITIATED: "eBay refunded the buyer",
  CLOSED: "Closed",
};

/** "FOUND_BETTER_PRICE" → "Found better price", for anything eBay adds that we don't know yet. */
function humanise(code: string) {
  const words = code.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function stateLabel(state: string | null | undefined): string {
  if (!state) return tr("No eBay state");
  const known = STATE_LABELS[state];
  return known ? tr(known) : humanise(state);
}

export type StateTone = "amber" | "green" | "red" | "grey";

/** Amber = waiting on us, green = back with us, red = declined / eBay refunded, grey = everything else. */
export function stateTone(state: string | null | undefined): StateTone {
  switch (state) {
    case "RETURN_REQUESTED":
    case "RMA_PENDING":
    case "RETURN_LABEL_REQUESTED":
    case "RETURN_LABEL_PENDING":
      return "amber";
    case "ITEM_DELIVERED":
      return "green";
    case "RETURN_REJECTED":
    case "AUTO_REFUND_INITIATED":
      return "red";
    default:
      return "grey";
  }
}

const STAGE_LABELS: Record<ReturnStage, string> = {
  Requested: "Requested",
  Responded: "Responded",
  InTransit: "In transit",
  Received: "Received",
  Inspected: "Inspected",
  Refunded: "Refunded",
  Closed: "Closed",
};

export function stageLabel(stage: ReturnStage | string): string {
  const known = STAGE_LABELS[stage as ReturnStage];
  return known ? tr(known) : humanise(stage);
}

export interface IReturnReason {
  code: string;
  label: string;
  group: ReturnReasonGroup;
}

/**
 * eBay's `ReturnReasonEnum` names with eBay's own wording (spec §3), in the groups the spec uses.
 * The same list is offered when a PartPro or counter return is logged by hand, so every return
 * has a reason eBay would recognise.
 */
export const RETURN_REASONS: IReturnReason[] = [
  {
    code: "ARRIVED_DAMAGED",
    label: "Arrived damaged",
    group: "Not as described",
  },
  {
    code: "DEFECTIVE_ITEM",
    label: "Item defective",
    group: "Not as described",
  },
  {
    code: "NOT_AS_DESCRIBED",
    label: "Item didn't match description",
    group: "Not as described",
  },
  {
    code: "DIFFERENT_FROM_LISTING",
    label: "Different from listing",
    group: "Not as described",
  },
  { code: "MISSING_PARTS", label: "Missing parts", group: "Not as described" },
  {
    code: "ORDERED_DIFFERENT_ITEM",
    label: "Received the wrong item",
    group: "Not as described",
  },
  {
    code: "DOES_NOT_FIT",
    label: "Doesn't fit my vehicle",
    group: "Doesn't fit",
  },
  {
    code: "NO_LONGER_NEED_ITEM",
    label: "No longer needed",
    group: "Changed mind",
  },
  {
    code: "ORDERED_ACCIDENTALLY",
    label: "Ordered by accident",
    group: "Changed mind",
  },
  {
    code: "ORDERED_WRONG_ITEM",
    label: "Ordered the wrong item",
    group: "Changed mind",
  },
  {
    code: "FOUND_BETTER_PRICE",
    label: "Found a better price",
    group: "Changed mind",
  },
  { code: "NO_REASON", label: "No reason given", group: "Changed mind" },
];

/** Other spellings eBay has used for the same reason. */
const REASON_ALIASES: Record<string, string> = {
  // Older spellings seen in the wild map onto eBay's current ReturnReasonEnum names.
  WRONG_SIZE: "DOES_NOT_FIT",
  DOESNT_FIT: "DOES_NOT_FIT",
  RECEIVED_WRONG_ITEM: "ORDERED_DIFFERENT_ITEM",
  WRONG_ITEM: "ORDERED_DIFFERENT_ITEM",
  SKU_NOT_AS_DESCRIBED: "NOT_AS_DESCRIBED",
};

function findReason(
  reason: string | null | undefined,
): IReturnReason | undefined {
  if (!reason) return undefined;
  const code = REASON_ALIASES[reason] ?? reason;
  return RETURN_REASONS.find((r) => r.code === code);
}

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return tr("No reason given");
  const known = findReason(reason);
  return known ? tr(known.label) : humanise(reason);
}

/** eBay's `reasonType` decides the group when the reason itself isn't one we know. */
export function reasonGroup(
  reason: string | null | undefined,
  reasonType?: string | null,
): ReturnReasonGroup {
  const known = findReason(reason);
  if (known) return known.group;
  if (reasonType === "SNAD") return "Not as described";
  if (reasonType === "REMORSE") return "Changed mind";
  return "Other";
}

export function reasonGroupLabel(group: ReturnReasonGroup): string {
  return tr(group);
}

/** The reasons in the order the spec lists the groups, for a grouped dropdown. */
export function reasonsByGroup(): {
  group: ReturnReasonGroup;
  reasons: IReturnReason[];
}[] {
  const order: ReturnReasonGroup[] = [
    "Not as described",
    "Changed mind",
    "Doesn't fit",
  ];
  return order.map((group) => ({
    group,
    reasons: RETURN_REASONS.filter((r) => r.group === group),
  }));
}

export interface DeadlineText {
  /** "22h left", "2d 2h left", "Overdue 20h". */
  text: string;
  /** "Respond by Fri 30 Oct, 09:41". */
  sub: string;
  tone: ReturnDeadlineTone;
}

const DEADLINE_LABELS: Record<ReturnDeadlineKind, string> = {
  respond: "Respond by",
  post: "Buyer to post by",
  buyer_respond: "Buyer to respond by",
  refund: "Refund by",
};

/** "2d 2h", "22h", "45m" – whole units, no seconds. */
function spanText(minutes: number): string {
  const total = Math.max(0, Math.floor(minutes));
  const days = Math.floor(total / (60 * 24));
  const hours = Math.floor((total % (60 * 24)) / 60);
  const mins = total % 60;
  if (days > 0)
    return hours > 0
      ? tr("{{days}}d {{hours}}h", { days, hours })
      : tr("{{days}}d", { days });
  if (hours > 0) return tr("{{hours}}h", { hours });
  return tr("{{minutes}}m", { minutes: Math.max(1, mins) });
}

/**
 * The countdown chip for the deadline that applies now. The tone is worked out from `now` here
 * (grey > a day away, amber inside a day, red once past) rather than taken from the API, which
 * only knew the time it last synced. eBay's own date goes underneath, never recalculated.
 */
export function deadlineText(
  deadline: ReturnDeadline | null | undefined,
  now: DateTime,
): DeadlineText | null {
  if (!deadline || !deadline.kind || !deadline.at) return null;
  const at = DateTime.fromISO(deadline.at, { zone: "utc" });
  if (!at.isValid) return null;
  const label = deadline.label || tr(DEADLINE_LABELS[deadline.kind]);
  // Posting deadlines are a day on eBay, so no time of day.
  const when = formatDateTime(
    deadline.at,
    deadline.kind === "post" ? INBOX_DAY : INBOX_DAY_TIME,
  );
  const sub = `${label} ${when}`;
  const minutesLeft = at.diff(now, "minutes").minutes;
  // The buyer's own deadlines are theirs to miss, not ours: never amber or red.
  const buyers = deadline.kind === "post" || deadline.kind === "buyer_respond";
  if (minutesLeft < 0)
    return buyers
      ? { text: tr("Buyer late by {{time}}", { time: spanText(-minutesLeft) }), sub, tone: "grey" }
      : { text: tr("Overdue {{time}}", { time: spanText(-minutesLeft) }), sub, tone: "red" };
  const tone: ReturnDeadlineTone = !buyers && minutesLeft < 60 * 24 ? "amber" : "grey";
  return {
    text: tr("{{time}} left", { time: spanText(minutesLeft) }),
    sub,
    tone,
  };
}

export interface DeductionBand {
  condition: ReturnCondition;
  min: number;
  max: number;
}

/** eBay's published condition bands: % of the item price only, never postage (spec §3). */
export const DEDUCTION_BANDS: DeductionBand[] = [
  { condition: "Excellent", min: 0, max: 0 },
  { condition: "Good", min: 5, max: 10 },
  { condition: "Fair", min: 15, max: 30 },
  { condition: "Poor", min: 35, max: 50 },
];

export function bandFor(condition: ReturnCondition): DeductionBand {
  return (
    DEDUCTION_BANDS.find((b) => b.condition === condition) ?? DEDUCTION_BANDS[0]
  );
}

/** The band a percentage sits in; above a band's top rounds up to the next (eBay has no gaps). */
export function bandForPct(pct: number): DeductionBand {
  return (
    DEDUCTION_BANDS.find((b) => pct <= b.max) ??
    DEDUCTION_BANDS[DEDUCTION_BANDS.length - 1]
  );
}

export function conditionLabel(condition: ReturnCondition): string {
  return tr(condition);
}

/** "0%", "5–10%". */
export function bandRangeText(band: DeductionBand): string {
  return band.min === band.max ? `${band.max}%` : `${band.min}–${band.max}%`;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** What the buyer gets back: the item less the deduction, plus the original postage in full. */
export function refundAfterDeduction(
  item: number,
  postage: number,
  pct: number,
): number {
  const safePct = Math.min(100, Math.max(0, pct || 0));
  return round2(round2(item * (1 - safePct / 100)) + (postage || 0));
}

/** The deduction that produces a given refund (to 2 dp, so a typed amount isn't rounded away), so the two inputs can be kept in step either way. */
export function deductionPctFor(
  item: number,
  postage: number,
  refund: number,
): number {
  if (!item) return 0;
  const pct = ((item - (refund - (postage || 0))) / item) * 100;
  return round2(Math.min(100, Math.max(0, pct)));
}

const ACTION_LABELS: Record<ReturnActionKind, string> = {
  accept: "Accept the return",
  full_refund_keep: "Full refund, buyer keeps the item",
  partial_offer: "Offer a partial refund, buyer keeps the item",
  decline: "Decline the return",
  provide_rma: "Provide an RMA number",
  upload_label: "Upload your own return label",
  mark_received: "Mark received",
  refund_full: "Refund in full",
  refund_deduction: "Refund with a deduction",
  message: "Send a message",
  escalate: "Ask eBay to step in",
};

export function actionLabel(kind: ReturnActionKind): string {
  return tr(ACTION_LABELS[kind]);
}

export type ActionTone = "primary" | "default" | "danger";

/** Primary = the thing most returns need next; danger = red, spec §9 (Decline, eBay stepping in). */
export function actionTone(kind: ReturnActionKind): ActionTone {
  switch (kind) {
    case "accept":
    case "provide_rma":
    case "mark_received":
    case "refund_full":
      return "primary";
    case "decline":
    case "escalate":
      return "danger";
    default:
      return "default";
  }
}

/** Fallback copy from the design; the API's own `whatHappensNext[kind]` wins when present. */
const WHAT_HAPPENS_NEXT: Record<ReturnActionKind, string> = {
  accept:
    "The buyer gets an eBay return label and has until eBay's send-by date to post it. Once it's delivered back you have 2 business days to refund, or eBay refunds the buyer automatically.",
  full_refund_keep:
    "eBay refunds the buyer in full and closes the return. The buyer keeps the item; nothing comes back.",
  partial_offer:
    "The buyer can accept (refund issued, return closed) or decline. Either side can ask eBay to step in after 3 business days.",
  decline:
    "The return is closed as declined. The buyer can ask eBay to step in within 3 business days.",
  provide_rma:
    "eBay gives the buyer a return label and the return proceeds as accepted — the buyer posts it by eBay's send-by date, then you have 2 business days to refund once delivered back.",
  upload_label:
    "Your label replaces eBay's. The buyer prints it from the return and has until eBay's send-by date to post it.",
  mark_received:
    "eBay records the item as delivered back and starts the 2-business-day refund clock.",
  refund_full:
    "eBay refunds the buyer the full amount, including the original postage, and closes the return.",
  refund_deduction:
    "eBay refunds the buyer the reduced amount and closes the return. Deductions recover lost value only — never postage or restocking.",
  message:
    "The buyer sees your message on the return on eBay. Nothing else changes.",
  escalate:
    "eBay reviews the return and decides. Once eBay has stepped in the options here become read-only.",
};

export function whatHappensNext(
  kind: ReturnActionKind,
  detail: Pick<ReturnDetail, "whatHappensNext"> | null | undefined,
): string {
  return detail?.whatHappensNext?.[kind] || tr(WHAT_HAPPENS_NEXT[kind]);
}

export interface TemplateExtras {
  shop_name?: string;
  refund_amount?: string;
  rma?: string;
  deadline?: string;
}

/**
 * Fills a template's merge fields ({buyer_first_name} {part_title} {refund_amount} {deadline}
 * {shop_name} {rma}) from the return. The API already fills `detail.templates`; this is for
 * re-filling when the amount or RMA changes in the panel.
 */
export function mergeReturnTemplate(
  body: string,
  detail: ReturnDetail,
  extras: TemplateExtras = {},
): string {
  const refund = detail.estimatedRefund
    ? PriceFormat(detail.estimatedRefund.total)
    : undefined;
  return fillMergeFields(body, {
    buyer_first_name:
      buyerFirstName(detail.order?.deliveryName) ?? detail.buyer.username,
    part_title: detail.part?.title ?? null,
    refund_amount: extras.refund_amount ?? refund,
    deadline:
      extras.deadline ??
      (detail.deadline.at ? buyerFacingDate(detail.deadline.at) : undefined),
    shop_name: extras.shop_name,
    rma: extras.rma ?? detail.rma,
  });
}

/** Template keys that belong to an action, in the order they're offered. */
export function templateKeysFor(kind: ReturnActionKind): string[] {
  switch (kind) {
    case "accept":
      return ["accept", "accept_damaged", "accept_doesnt_fit"];
    case "full_refund_keep":
      return ["full_refund_keep"];
    case "partial_offer":
      return ["partial_offer"];
    case "decline":
      return ["decline"];
    case "mark_received":
      return ["received"];
    case "refund_full":
      return ["refund_full"];
    case "refund_deduction":
      return ["refund_deduction"];
    default:
      return [];
  }
}

/** "accept_damaged" → "Accept – damaged". */
export function templateLabel(key: string): string {
  const [first, ...rest] = key.split("_");
  const head = first.charAt(0).toUpperCase() + first.slice(1);
  return rest.length ? `${head} – ${rest.join(" ")}` : head;
}

/** eBay states in which the part is on its way back or already back, so it can be checked in. */
const ON_ITS_WAY_OR_BACK = new Set([
  "ITEM_READY_TO_SHIP",
  "ITEM_SHIPPED",
  "ITEM_DELIVERED",
]);

/**
 * Whether the return page shows "Received and checked": not checked yet, not closed, eBay hasn't
 * stepped in, and the part is coming back (eBay) or the case is simply open (PartPro, counter).
 */
export function showsReceivedCheck(
  detail: Pick<
    ReturnDetail,
    "channel" | "ebayState" | "stage" | "escalated" | "inspection"
  >,
): boolean {
  if (detail.inspection || detail.escalated) return false;
  if (detail.stage === "Closed" || detail.ebayState === "CLOSED") return false;
  if (detail.channel !== "ebay") return true;
  const state = detail.ebayState ?? "";
  return ON_ITS_WAY_OR_BACK.has(state) || state.startsWith("RETURN_LABEL_");
}

/** Open and not handed to eBay: the return tracking can still be added or changed. */
export function canAddReturnTracking(
  detail: Pick<ReturnDetail, "stage" | "ebayState" | "escalated">,
): boolean {
  return (
    !detail.escalated &&
    detail.stage !== "Closed" &&
    detail.ebayState !== "CLOSED"
  );
}

export interface RefundNowPrefill {
  kind: "refund_full" | "refund_deduction";
  condition: ReturnCondition;
  /** Inside the condition's band (its top, as the refund panel defaults to); 0 for a full refund. */
  deductionPct: number;
}

/**
 * What "Refund now" opens after a check: a full refund for Excellent, otherwise a deduction set
 * inside the band that was checked. Only an action eBay offers right now is chosen (the other
 * one if that's all there is), and a deduction needs eBay to have itemised the refund. Null =
 * eBay doesn't offer a refund yet.
 */
export function refundNowPrefill(
  condition: ReturnCondition,
  allowedActions: ReturnActionKind[],
  itemised: boolean,
): RefundNowPrefill | null {
  const full = allowedActions.includes("refund_full");
  const deduction = allowedActions.includes("refund_deduction") && itemised;
  const band = bandFor(condition);
  if (condition === "Excellent" || !deduction)
    return full
      ? { kind: "refund_full", condition, deductionPct: 0 }
      : deduction
        ? { kind: "refund_deduction", condition, deductionPct: band.max }
        : null;
  return { kind: "refund_deduction", condition, deductionPct: band.max };
}

/** The line "Refund later" leaves on the page. */
export function refundLaterText(
  refundDueBy: string | null | undefined,
  channel: string = "ebay",
): string {
  if (refundDueBy)
    return tr(
      "It's in Refund due — eBay's deadline is {{when}}, or eBay refunds it for you.",
      { when: formatDateTime(refundDueBy, INBOX_DAY_TIME) },
    );
  if (channel !== "ebay")
    return tr("Refund it from this page when you're ready.");
  return tr(
    "eBay hasn't set a refund deadline yet. It does once eBay shows the return as delivered back, and if that deadline is missed eBay refunds it for you.",
  );
}

/**
 * Where a bell row goes. The warehouse phone pages are gone, so an older row pointing at one
 * opens the return itself (or the list when there's no id in it).
 */
export function returnNotificationHref(href: string): string {
  const match = /^\/returns\/warehouse(?:\/([^/?#]+))?/.exec(href);
  if (!match) return href;
  const id = match[1];
  return id && !["to-locate", "todo"].includes(id)
    ? `/returns/${id}`
    : "/returns";
}
